import React from 'react';
import { Button } from './Button';
import { Zap, TrendingUp, BarChart3 } from 'lucide-react';
import { Link } from './Router';

export const Hero: React.FC = () => {
  // SVG Logos for "Trust" section to look professional
  const TrustLogo = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 100 30" fill="currentColor">
      <path d="M10,15 L20,5 L30,15 L20,25 Z M40,5 H50 V25 H40 Z M60,5 H80 V10 H65 V12 H75 V17 H65 V25 H60 Z" />
    </svg>
  );

  return (
    <section className="relative overflow-hidden pt-12 pb-20 lg:pt-24 lg:pb-40 bg-brand-gray">
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          
          {/* Text Content */}
          <div className="lg:w-1/2 text-center lg:text-left">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-brand-orange to-red-500 text-white px-6 py-2 rounded-full font-black text-xs uppercase tracking-widest mb-8 animate-fade-in-up opacity-0 shadow-lg shadow-orange-500/30 transform -rotate-2 mx-auto lg:mx-0">
              <Zap size={14} className="fill-white" />
              Ready for Growth
            </div>
            
            <h1 className="text-6xl sm:text-7xl md:text-[5.5rem] font-black leading-[0.9] mb-8 text-brand-dark tracking-tighter uppercase animate-fade-in-up-delay-1 opacity-0">
              Marketing <br/>
              auf <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-orange to-red-600">ZACK.</span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-500 mb-10 leading-relaxed font-medium max-w-xl mx-auto lg:mx-0 animate-fade-in-up-delay-2 opacity-0">
              Schluss mit 08/15. Wir liefern High-Speed Content, der ballert und Websites, die Besucher in Cash verwandeln. Aggressives Wachstum statt langweiliger Standards.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-16 animate-fade-in-up-delay-3 opacity-0">
              <Link to="/kontakt" className="w-full sm:w-auto">
                <Button variant="primary" className="w-full text-lg shadow-2xl shadow-orange-500/40">
                  Kostenloses Erstgespräch
                </Button>
              </Link>
              <Link to="/cases" className="w-full sm:w-auto">
                <Button variant="secondary" className="w-full text-lg">
                  Cases ansehen
                </Button>
              </Link>
            </div>

            {/* Trust Strip */}
            <div className="pt-8 border-t border-gray-100 animate-fade-in-up opacity-0" style={{ animationDelay: '0.6s' }}>
               <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">Uns vertrauen die Besten</p>
               <div className="flex flex-wrap justify-center lg:justify-start gap-8 opacity-60 grayscale transition-all duration-500 ease-apple">
                  {/* Mockup Logos */}
                  <img src="https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg" className="h-6 md:h-8 object-contain opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300 ease-apple" alt="Trust Logo" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" className="h-6 md:h-8 object-contain opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300 ease-apple" alt="Trust Logo" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg" className="h-6 md:h-8 object-contain opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300 ease-apple" alt="Trust Logo" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/e/e8/Tesla_logo.png" className="h-6 md:h-8 object-contain opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300 ease-apple" alt="Trust Logo" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg" className="h-6 md:h-8 object-contain opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300 ease-apple" alt="Trust Logo" />
               </div>
            </div>
          </div>

          {/* Visual Content */}
          <div className="lg:w-1/2 w-full relative flex justify-center lg:justify-end animate-scale-in opacity-0" style={{ animationDelay: '0.4s' }}>
            <div className="relative w-full max-w-[500px] aspect-[4/5] md:aspect-square">
               {/* Main Abstract Card */}
               <div className="absolute inset-0 bg-[#0a0a0a] rounded-[3rem] rotate-3 hover:rotate-1 transition-transform duration-1000 ease-apple shadow-2xl overflow-hidden border border-gray-800 group">
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black opacity-90"></div>
                  {/* Glowing orb */}
                  <div className="absolute -right-20 -top-20 w-96 h-96 bg-brand-orange rounded-full blur-[120px] opacity-30 group-hover:opacity-40 transition-opacity duration-1000 ease-apple"></div>
                  
                  {/* Decorative Elements inside card */}
                  <div className="relative h-full flex flex-col items-center justify-center p-8">
                      <h3 className="text-white/5 text-[10rem] font-black absolute -bottom-10 -right-10 leading-none select-none overflow-hidden">$$$</h3>
                      
                      <div className="relative z-10 w-full">
                          <div className="bg-white/10 backdrop-blur-md border border-white/10 p-6 rounded-2xl mb-4 transform translate-x-4 transition-transform duration-700 ease-apple group-hover:translate-x-2">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-gray-400 text-xs font-bold uppercase">Revenue</span>
                                <TrendingUp className="text-green-400 w-4 h-4" />
                            </div>
                            <div className="text-4xl font-black text-white">€ 124.5k</div>
                            <div className="text-green-400 text-xs font-bold mt-1">+ 42% vs last month</div>
                          </div>

                          <div className="bg-gradient-to-r from-brand-orange to-red-600 p-6 rounded-2xl transform -translate-x-4 shadow-xl shadow-orange-900/50 transition-transform duration-700 ease-apple group-hover:-translate-x-2">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-white/80 text-xs font-bold uppercase">Leads Generated</span>
                                <BarChart3 className="text-white w-4 h-4" />
                            </div>
                            <div className="text-4xl font-black text-white">843</div>
                            <div className="w-full bg-black/20 h-1 mt-3 rounded-full overflow-hidden">
                                <div className="bg-white h-full w-[85%]"></div>
                            </div>
                          </div>
                      </div>
                  </div>
               </div>

               {/* Floating Badge */}
               <div className="absolute top-10 -left-4 md:-left-12 bg-white p-5 rounded-2xl shadow-xl border-l-8 border-brand-orange animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
                  <p className="text-xs text-gray-500 font-bold uppercase mb-1">Conversion Rate</p>
                  <p className="text-brand-dark font-black text-3xl">+ 142%</p>
               </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};